import type {
  CreateDemoScheduleBlockRequest,
  CreateDemoScheduledIntakeInvitationRequest,
  CreateSchedulingAppointmentRequest,
  DemoAppointment,
  DemoAppointmentMutationReceipt,
  DemoAvailabilityQuery,
  DemoAvailabilityResponse,
  DemoReminderChannel,
  DemoReminderKind,
  DemoReminderReceipt,
  DemoScheduleBlock,
  DemoScheduledIntakeInvitationReceipt,
  DemoSchedulingConfiguration,
  IssueVisitIntakeLinkRequest,
  SaveProviderAvailabilityRequest,
  SchedulingSaveResult,
  SchedulingViewer,
  VisitIntakeLinkResponse,
} from "@emr/contracts";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type {
  DemoSchedulingClient,
  SchedulingAppointmentUpdate,
  SchedulingClientState,
  SchedulingSaveOptions,
  SchedulingStatusUpdate,
} from "./demoSchedulingClient";
import { isSchedulingPermissionError, isStaleSchedulingSave } from "./schedulingErrors";

export { createDemoSchedulingIdempotencyKey } from "./demoSchedulingClient";

interface DemoSchedulingContextValue {
  /**
   * Re-issues a booking's patient links, passing the options on ("Ask for patient details",
   * PA-10), then re-reads the schedule. The links are in the response only, and are returned
   * even when that read fails: the earlier links no longer work, so losing these would leave the
   * patient with none.
   */
  readonly issuePublicTokens?: DemoSchedulingClient["issuePublicTokens"];
  readonly revokePublicTokens?: DemoSchedulingClient["revokePublicTokens"];
  /**
   * Issues a visit's questionnaire link (PA-5), then re-reads the schedule so rows show the new
   * link. Only durable clinics have it. The token is in the response only: never in state.
   */
  readonly issueVisitIntakeLink?:
    | ((
        encounterId: string,
        request: IssueVisitIntakeLinkRequest,
      ) => Promise<VisitIntakeLinkResponse>)
    | undefined;
  /**
   * Corrects a booking's phone or email (owner decision O4, PA-7) and puts the returned booking
   * in state. Only durable clinics have it, so its presence also says the durable schedule lane
   * is the one in use (Attention asks for patient-message rows only then).
   */
  readonly correctBookingContact?: DemoSchedulingClient["correctBookingContact"];
  readonly state: SchedulingClientState | null;
  /** What the signed-in staff member may change; null until the schedule first loads. */
  readonly viewer: SchedulingViewer | null;
  readonly isLoading: boolean;
  readonly error: string | null;
  refresh(): Promise<void>;
  getAvailability(query: DemoAvailabilityQuery): Promise<DemoAvailabilityResponse>;
  createAppointment(
    input: CreateSchedulingAppointmentRequest,
  ): Promise<DemoAppointmentMutationReceipt>;
  updateAppointment(
    appointmentId: string,
    input: SchedulingAppointmentUpdate,
  ): Promise<DemoAppointment>;
  updateAppointmentStatus(
    appointmentId: string,
    input: SchedulingStatusUpdate,
  ): Promise<DemoAppointment>;
  sendReminder(
    appointmentId: string,
    input: {
      readonly kind: DemoReminderKind;
      readonly channel: DemoReminderChannel;
      readonly idempotencyKey: string;
    },
  ): Promise<DemoReminderReceipt>;
  createScheduledIntakeInvitation(
    appointmentId: string,
    input: CreateDemoScheduledIntakeInvitationRequest,
  ): Promise<DemoScheduledIntakeInvitationReceipt>;
  /**
   * Saves the clinic settings (never the physician calendars). "saved" replaces
   * state.configuration; "acknowledgement_required" saves nothing and changes no state.
   */
  updateConfiguration(
    configuration: DemoSchedulingConfiguration,
    options?: SchedulingSaveOptions,
  ): Promise<SchedulingSaveResult>;
  /** Saves one physician's calendar; the result is handled as for updateConfiguration. */
  saveProviderAvailability(
    providerId: string,
    request: SaveProviderAvailabilityRequest,
  ): Promise<SchedulingSaveResult>;
  createBlock(input: CreateDemoScheduleBlockRequest): Promise<DemoScheduleBlock>;
  removeBlock(block: DemoScheduleBlock): Promise<void>;
}

const DemoSchedulingContext = createContext<DemoSchedulingContextValue | null>(null);

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "The clinic schedule could not be updated.";
}

export function DemoSchedulingProvider({
  children,
  client,
  pollIntervalMs = 4_000,
}: {
  readonly children: ReactNode;
  readonly client: DemoSchedulingClient;
  readonly pollIntervalMs?: number;
}) {
  const [state, setState] = useState<SchedulingClientState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(false);
  /*
   * Reads can overlap (the poll, a refresh after a change, the tab coming back). A read that
   * started before one already applied is older, so it never replaces newer state: a link issued
   * a moment ago must not vanish under a slow poll that began before it.
   */
  const readsStarted = useRef(0);
  const readApplied = useRef(0);

  const refresh = useCallback(async () => {
    const read = ++readsStarted.current;
    try {
      const next = await client.getState();
      if (!mounted.current || read < readApplied.current) return;
      readApplied.current = read;
      setState(next);
      setError(null);
    } catch (caught) {
      if (mounted.current) setError(errorMessage(caught));
      throw caught;
    } finally {
      if (mounted.current) setIsLoading(false);
    }
  }, [client]);

  useEffect(() => {
    mounted.current = true;
    void refresh().catch(() => undefined);
    const interval =
      pollIntervalMs > 0
        ? window.setInterval(() => void refresh().catch(() => undefined), pollIntervalMs)
        : undefined;
    /*
     * Browsers slow timers in a hidden tab to about once a minute, so a desk that switched tabs
     * would see a patient's answers late. Read again as soon as the tab is shown.
     */
    const onVisible = () => {
      if (document.visibilityState === "visible") void refresh().catch(() => undefined);
    };
    if (pollIntervalMs > 0) document.addEventListener("visibilitychange", onVisible);
    return () => {
      mounted.current = false;
      if (interval !== undefined) window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [pollIntervalMs, refresh]);

  const getAvailability = useCallback(
    (query: DemoAvailabilityQuery) => client.getAvailability(query),
    [client],
  );

  const createAppointment = useCallback(
    async (input: CreateSchedulingAppointmentRequest) => {
      try {
        const receipt = await client.createAppointment(input);
        if (mounted.current) {
          setState((current) =>
            current === null
              ? current
              : {
                  ...current,
                  appointments: [...current.appointments, receipt.appointment],
                  reminderReceipts: [...current.reminderReceipts, ...receipt.reminders],
                },
          );
          setError(null);
        }
        return receipt;
      } catch (caught) {
        if (mounted.current) setError(errorMessage(caught));
        throw caught;
      }
    },
    [client],
  );

  const updateAppointment = useCallback(
    async (appointmentId: string, input: SchedulingAppointmentUpdate) => {
      try {
        const appointment = await client.updateAppointment(appointmentId, input);
        if (mounted.current) {
          setState((current) =>
            current === null
              ? current
              : {
                  ...current,
                  appointments: current.appointments.map((candidate) =>
                    candidate.id === appointment.id ? appointment : candidate,
                  ),
                },
          );
          setError(null);
        }
        return appointment;
      } catch (caught) {
        if (mounted.current) setError(errorMessage(caught));
        throw caught;
      }
    },
    [client],
  );

  const updateAppointmentStatus = useCallback(
    async (appointmentId: string, input: SchedulingStatusUpdate) => {
      try {
        const appointment = await client.updateAppointmentStatus(appointmentId, input);
        if (mounted.current) {
          setState((current) =>
            current === null
              ? current
              : {
                  ...current,
                  appointments: current.appointments.map((candidate) =>
                    candidate.id === appointment.id ? appointment : candidate,
                  ),
                },
          );
          setError(null);
        }
        return appointment;
      } catch (caught) {
        if (mounted.current) setError(errorMessage(caught));
        throw caught;
      }
    },
    [client],
  );

  const sendReminder = useCallback(
    async (
      appointmentId: string,
      input: {
        readonly kind: DemoReminderKind;
        readonly channel: DemoReminderChannel;
        readonly idempotencyKey: string;
      },
    ) => {
      try {
        const receipt = await client.sendReminder(appointmentId, input);
        if (mounted.current) {
          setState((current) =>
            current === null
              ? current
              : {
                  ...current,
                  reminderReceipts: current.reminderReceipts.some(
                    (candidate) => candidate.id === receipt.id,
                  )
                    ? current.reminderReceipts
                    : [...current.reminderReceipts, receipt],
                },
          );
          setError(null);
        }
        return receipt;
      } catch (caught) {
        if (mounted.current) setError(errorMessage(caught));
        throw caught;
      }
    },
    [client],
  );

  const createScheduledIntakeInvitation = useCallback(
    async (appointmentId: string, input: CreateDemoScheduledIntakeInvitationRequest) => {
      try {
        const receipt = await client.createScheduledIntakeInvitation(appointmentId, input);
        /*
         * The invitation receipt intentionally does not expose the appointment's
         * new concurrency timestamp. Reload the server projection so a subsequent
         * check-in or reschedule cannot submit a stale expectedUpdatedAt value.
         */
        const next = await client.getState();
        if (mounted.current) {
          setState(next);
          setError(null);
        }
        return receipt;
      } catch (caught) {
        if (mounted.current) setError(errorMessage(caught));
        throw caught;
      }
    },
    [client],
  );

  /*
   * Someone saved first, or the viewer's role changed: re-read before the caller reacts, so a
   * reload starts from the current version and the screen offers only what is still allowed.
   */
  const recordChangeFailure = useCallback(
    async (caught: unknown) => {
      if (isStaleSchedulingSave(caught) || isSchedulingPermissionError(caught)) {
        await refresh().catch(() => undefined);
      }
      if (mounted.current) setError(errorMessage(caught));
    },
    [refresh],
  );

  const applySave = useCallback(
    async (save: () => Promise<SchedulingSaveResult>) => {
      try {
        const result = await save();
        if (mounted.current) {
          if (result.status === "saved") {
            setState((current) =>
              current === null ? current : { ...current, configuration: result.configuration },
            );
          }
          setError(null);
        }
        return result;
      } catch (caught) {
        await recordChangeFailure(caught);
        throw caught;
      }
    },
    [recordChangeFailure],
  );

  const updateConfiguration = useCallback(
    (configuration: DemoSchedulingConfiguration, options?: SchedulingSaveOptions) =>
      applySave(() => client.updateConfiguration(configuration, options)),
    [applySave, client],
  );

  const saveProviderAvailability = useCallback(
    (providerId: string, request: SaveProviderAvailabilityRequest) =>
      applySave(() => client.saveProviderAvailability(providerId, request)),
    [applySave, client],
  );

  const createBlock = useCallback(
    async (input: CreateDemoScheduleBlockRequest) => {
      try {
        const block = await client.createBlock(input);
        if (mounted.current) {
          setState((current) =>
            current === null ? current : { ...current, blocks: [...current.blocks, block] },
          );
          setError(null);
        }
        return block;
      } catch (caught) {
        await recordChangeFailure(caught);
        throw caught;
      }
    },
    [client, recordChangeFailure],
  );

  const removeBlock = useCallback(
    async (block: DemoScheduleBlock) => {
      try {
        await client.removeBlock(block);
        if (mounted.current) {
          setState((current) =>
            current === null
              ? current
              : {
                  ...current,
                  blocks: current.blocks.filter((candidate) => candidate.id !== block.id),
                },
          );
          setError(null);
        }
      } catch (caught) {
        await recordChangeFailure(caught);
        throw caught;
      }
    },
    [client, recordChangeFailure],
  );

  const correctContact = client.correctBookingContact;
  const correctBookingContact = useMemo<DemoSchedulingClient["correctBookingContact"]>(
    () =>
      correctContact === undefined
        ? undefined
        : async (appointmentId, request, signal) => {
            try {
              const appointment = await correctContact(appointmentId, request, signal);
              if (mounted.current) {
                setState((current) =>
                  current === null
                    ? current
                    : {
                        ...current,
                        appointments: current.appointments.map((candidate) =>
                          candidate.id === appointment.id ? appointment : candidate,
                        ),
                      },
                );
              }
              return appointment;
            } catch (caught) {
              // A stale version: re-read so the next try starts from the booking as it is now.
              await refresh().catch(() => undefined);
              throw caught;
            }
          },
    [correctContact, refresh],
  );
  const issuePublicTokens = client.issuePublicTokens;
  const revokePublicTokens = client.revokePublicTokens;
  const issueVisitIntakeLink = client.issueVisitIntakeLink;
  const value = useMemo<DemoSchedulingContextValue>(
    () => ({
      issueVisitIntakeLink:
        issueVisitIntakeLink === undefined
          ? undefined
          : async (encounterId, request) => {
              const result = await issueVisitIntakeLink(encounterId, request);
              // The link exists whether or not this read succeeds; the poll catches up.
              await refresh().catch(() => undefined);
              return result;
            },
      issuePublicTokens:
        issuePublicTokens === undefined
          ? undefined
          : async (id, version, options) => {
              // Pass the options on only when given: a plain re-issue keeps its two arguments.
              const result =
                options === undefined
                  ? await issuePublicTokens(id, version)
                  : await issuePublicTokens(id, version, options);
              // The new links exist whether or not this read succeeds; the poll catches up.
              await refresh().catch(() => undefined);
              return result;
            },
      revokePublicTokens:
        revokePublicTokens === undefined
          ? undefined
          : async (id, version) => {
              await revokePublicTokens(id, version);
              await refresh();
            },
      correctBookingContact,
      state,
      viewer: state?.viewer ?? null,
      isLoading,
      error,
      refresh,
      getAvailability,
      createAppointment,
      updateAppointment,
      updateAppointmentStatus,
      sendReminder,
      createScheduledIntakeInvitation,
      updateConfiguration,
      saveProviderAvailability,
      createBlock,
      removeBlock,
    }),
    [
      correctBookingContact,
      issuePublicTokens,
      issueVisitIntakeLink,
      revokePublicTokens,
      createAppointment,
      createBlock,
      createScheduledIntakeInvitation,
      error,
      getAvailability,
      isLoading,
      refresh,
      removeBlock,
      saveProviderAvailability,
      sendReminder,
      state,
      updateAppointment,
      updateAppointmentStatus,
      updateConfiguration,
    ],
  );

  return <DemoSchedulingContext.Provider value={value}>{children}</DemoSchedulingContext.Provider>;
}

export function useDemoScheduling(): DemoSchedulingContextValue {
  const context = useContext(DemoSchedulingContext);
  if (context === null) {
    throw new Error("useDemoScheduling must be used inside DemoSchedulingProvider");
  }
  return context;
}

export function useOptionalDemoScheduling(): DemoSchedulingContextValue | null {
  return useContext(DemoSchedulingContext);
}
